import { useEffect } from "react"
import type { RefObject } from "react"

/**
 * Hard ceiling on a freeze, measured from the press start (OKS-1015 BRIEF UX
 * decision §2 exit d). A click or tap is ≈50–300 ms, so every normal press is
 * covered; a stuck freeze (lost `pointerup`, OS gesture) shows for at most this
 * long. Absolute — it also cuts {@link PRESS_FREEZE_NO_CLICK_GRACE_MS} short.
 */
export const PRESS_FREEZE_CEILING_MS = 1000

/**
 * How long a lifted press waits for its `click` before releasing anyway
 * (exit b — dragged off the target, text selection: no click will come).
 */
export const PRESS_FREEZE_NO_CLICK_GRACE_MS = 300

/**
 * `useFreezeHeightDuringPress` — hold an element at its current height for the
 * duration of a pointer press that starts OUTSIDE it, so content growing inside
 * it mid-press cannot move whatever the user is pressing below it.
 *
 * OKS-1015. `TiktokProfileEmbed`'s container has a 220px floor and no ceiling
 * (a cap would crop the creator's card — PO ruling, OKS-1007 BRIEF §5 Q2), and
 * TikTok's `embed.js` grows it out of band when it swaps the blockquote for its
 * `<iframe>`. If that lands between `pointerdown` and `pointerup` on a control
 * below (the contact panel's 16px channel radios, a home-triage card's
 * « Ouvrir »), the release hit-tests a different element, `click` fires on the
 * common ancestor, and the user's choice is silently lost. Pinning the
 * container's height for the press keeps the page below it still; the element
 * must be `overflow-hidden` so late growth is clipped for the press and shown in
 * full the moment the freeze releases. Nothing is capped at rest.
 *
 * **Start:** a window capture-phase `pointerdown` — primary pointer, primary
 * button, target not inside the element (a press inside it moves nothing below
 * it; presses inside a cross-origin iframe never reach this window anyway).
 * Writes `getBoundingClientRect().height` as an inline px height (border-box
 * under Tailwind preflight, so the box does not change size). A second press
 * while pinned keeps the first measurement.
 *
 * **Release — the first of:**
 * - (a) the press's `click` has been handled — released on a 0 ms timer
 *   scheduled from the capture-phase `click`, so the target's own handlers
 *   (Base UI commits a radio in its `onClick`) run while still pinned. Never on
 *   `pointerup`: on touch, the tap's `click` is dispatched in a LATER task,
 *   after a rendering step, so releasing at lift would let growth land before
 *   the tap is hit-tested;
 * - (b) lifted with no click — {@link PRESS_FREEZE_NO_CLICK_GRACE_MS} after lift;
 * - (c) `pointercancel` (a touch became a scroll, an OS gesture);
 * - (d) {@link PRESS_FREEZE_CEILING_MS} after the press start;
 * - (e) the window loses focus;
 * - (f) unmount.
 *
 * Only the height this hook wrote is ever cleared. The element must not carry a
 * React `style` prop: React re-applies it on re-render and would drop the pin
 * mid-press (the embed re-renders exactly then — hydration flips on iframe
 * injection).
 *
 * @param rootRef the element to freeze; read once, when the effect mounts.
 */
export function useFreezeHeightDuringPress<T extends HTMLElement>(
  rootRef: RefObject<T | null>,
): void {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return undefined

    let pinned = false
    let ceilingTimer: number | undefined
    let releaseTimer: number | undefined

    const clearTimers = () => {
      window.clearTimeout(ceilingTimer)
      window.clearTimeout(releaseTimer)
      ceilingTimer = undefined
      releaseTimer = undefined
    }

    const release = () => {
      clearTimers()
      if (!pinned) return
      pinned = false
      root.style.height = ""
    }

    const releaseAfter = (ms: number) => {
      if (!pinned) return
      window.clearTimeout(releaseTimer)
      releaseTimer = window.setTimeout(release, ms)
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!event.isPrimary || event.button !== 0) return
      if (event.target instanceof Node && root.contains(event.target)) return
      if (pinned) {
        // A new press while a release is pending keeps the pin; the ceiling
        // from the first press still bounds it.
        window.clearTimeout(releaseTimer)
        releaseTimer = undefined
        return
      }
      root.style.height = `${root.getBoundingClientRect().height}px`
      pinned = true
      ceilingTimer = window.setTimeout(release, PRESS_FREEZE_CEILING_MS)
    }

    const onPointerUp = (event: PointerEvent) => {
      if (!event.isPrimary) return
      releaseAfter(PRESS_FREEZE_NO_CLICK_GRACE_MS)
    }

    const onPointerCancel = (event: PointerEvent) => {
      if (!event.isPrimary) return
      releaseAfter(0)
    }

    const onClick = () => releaseAfter(0)

    // Bubble phase + target check: element `blur` does not bubble, so only the
    // window's own focus loss should reach here — a press moving focus between
    // elements must not release. Checked as "not a Node" rather than
    // `=== window`: jsdom's event target is not identical to its `window`
    // global, and an element target is always a Node in every environment.
    const onBlur = (event: FocusEvent) => {
      if (!(event.target instanceof Node)) releaseAfter(0)
    }

    window.addEventListener("pointerdown", onPointerDown, true)
    window.addEventListener("pointerup", onPointerUp, true)
    window.addEventListener("pointercancel", onPointerCancel, true)
    window.addEventListener("click", onClick, true)
    window.addEventListener("blur", onBlur)

    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true)
      window.removeEventListener("pointerup", onPointerUp, true)
      window.removeEventListener("pointercancel", onPointerCancel, true)
      window.removeEventListener("click", onClick, true)
      window.removeEventListener("blur", onBlur)
      release()
    }
  }, [rootRef])
}
